// src/components/HowItWorks.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lightbulb, CheckSquare, Tag, Star, Lock, FileText, Link as LinkIcon, BookOpen, Archive, Play, Pause, CheckCircle2, Sparkles, Rocket, Target, Zap, Crown } from 'lucide-react';

function HowItWorks() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            How Epiphoric Works
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Capture, organize, and track your creative ideas with complete privacy
          </p>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                Epiphoric is your secure, AI-powered idea management system. Whether you're tracking 
                project ideas, creative concepts, or personal goals, Epiphoric helps you capture, 
                develop, and bring your ideas to life — with optional AI assistance that transforms 
                rough concepts into actionable plans.
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary">
                  <Lock className="w-4 h-4" />
                  End-to-End Encrypted
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  <Sparkles className="w-4 h-4" />
                  AI-Powered
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                  <Zap className="w-4 h-4" />
                  Quick Capture
                </span>
              </div>
            </section>

            {/* Core Features */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Lightbulb className="w-6 h-6 text-primary" />
                Core Features
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-primary pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Idea Management
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Create and manage your ideas with rich descriptions. Each idea can include:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Detailed descriptions and notes</li>
                    <li>Status tracking (Drafted, In Progress, Completed, Paused, Archived)</li>
                    <li>5-star rating system for prioritization</li>
                    <li>Custom tags for easy categorization</li>
                  </ul>
                </div>

                <div className="border-l-4 border-primary pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-primary" />
                    TODO Lists
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Break down your ideas into actionable tasks:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Drag-and-drop task management</li>
                    <li>Mark tasks as complete</li>
                    <li>Attach URLs to tasks for proof of work or references</li>
                    <li>Organize tasks in any order you prefer</li>
                  </ul>
                </div>

                <div className="border-l-4 border-primary pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-primary" />
                    Tag System
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Organize your ideas with custom tags:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Create unlimited custom tags</li>
                    <li>Filter ideas by one or multiple tags</li>
                    <li>Quick access to related ideas</li>
                    <li>Tag-based navigation from anywhere</li>
                  </ul>
                </div>

                <div className="border-l-4 border-primary pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <LinkIcon className="w-5 h-5 text-primary" />
                    Resources
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Attach external references to your ideas:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Link to GitHub repositories</li>
                    <li>Reference design documents</li>
                    <li>Save research links</li>
                    <li>Keep all related resources in one place</li>
                  </ul>
                </div>

                <div className="border-l-4 border-primary pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    Lessons Learned
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Capture insights when you pause or archive projects:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Document what worked and what didn't</li>
                    <li>Save valuable insights for future reference</li>
                    <li>Learn from past projects</li>
                    <li>Build on your experience</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Status System */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Star className="w-6 h-6 text-primary" />
                Idea Status System
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Drafted</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    New ideas that haven't been started yet
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">In Progress</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Ideas you're actively working on
                  </p>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Pause className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Paused</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Ideas temporarily put on hold
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Completed</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Ideas that have been finished
                  </p>
                </div>

                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Archive className="w-5 h-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Archived</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Ideas moved to long-term storage
                  </p>
                </div>
              </div>
            </section>

            {/* AI-Powered Features */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-purple-500" />
                AI-Powered Development
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
                  <Crown className="w-3 h-3" aria-hidden />
                  Entitlement
                </span>
              </h2>
              
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6 mb-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <strong className="text-gray-900 dark:text-white">Supercharge your ideas with AI.</strong> Epiphoric&apos;s
                  AI enhancement features (when your account is entitled) help you transform rough concepts into structured, actionable plans in seconds.
                </p>
                <p className="text-sm">
                  <Link to="/ai-tools" className="text-primary font-medium hover:underline">
                    Read more: AI tools, access, and email verification →
                  </Link>
                </p>
              </div>

              <div className="space-y-6">
                <div className="border-l-4 border-purple-500 pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-purple-500" />
                    Autopilot Mode
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Let AI do the heavy lifting. Simply describe your idea and Autopilot will:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Generate a professional, clear title</li>
                    <li>Enhance and expand your description</li>
                    <li>Suggest relevant tags for organization</li>
                    <li>Create actionable subtasks and to-dos</li>
                    <li>Build a structured plan with logical steps</li>
                  </ul>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 italic">
                    Perfect for when you have a spark of an idea and want it fully developed in one click.
                  </p>
                </div>

                <div className="border-l-4 border-indigo-500 pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-500" />
                    Guided Journey
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Collaborate with AI step-by-step. The Guided Journey gives you full control:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li><strong>Step 1:</strong> Refine your title and description together</li>
                    <li><strong>Step 2:</strong> Review and customize tags and subtasks</li>
                    <li><strong>Step 3:</strong> Build your execution plan collaboratively</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 mt-3">
                    At each step, you can:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Edit the AI's suggestions directly</li>
                    <li>Provide feedback to regenerate with adjustments</li>
                    <li>Accept and continue when you're satisfied</li>
                  </ul>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 italic">
                    Ideal for complex ideas where you want to guide the AI and maintain creative control.
                  </p>
                </div>

                <div className="border-l-4 border-gray-400 pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-gray-500" />
                    Quick Capture
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Just want to save an idea quickly without AI? No problem:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Raw Save stores your idea immediately</li>
                    <li>Develop it later when you have time</li>
                    <li>Always available without using an AI enhancement credit</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Additional AI access */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Crown className="w-6 h-6 text-amber-500" aria-hidden />
                Need more than the complimentary credit?
              </h2>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  New accounts often receive <strong>one complimentary AI enhancement credit</strong>. For ongoing access, subscribe on the{' '}
                  <Link to="/support" className="text-primary hover:underline font-medium">Support page</Link>{' '}
                  for <strong>$10/month</strong> (100 enhancement credits per billing period).
                </p>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  How to reach us
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Create a free Epiphoric account</li>
                  <li>Open <Link to="/support" className="text-primary hover:underline font-medium">Support</Link> and describe your use case</li>
                  <li>We typically respond within 24–48 hours</li>
                </ul>
                <Link
                  to="/support"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  Contact Support
                </Link>
              </div>
            </section>

            {/* Security & Privacy */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Lock className="w-6 h-6 text-primary" />
                Security & Privacy
              </h2>
              
              <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 rounded-lg p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <strong className="text-gray-900 dark:text-white">Your data is encrypted at rest.</strong> All sensitive 
                  information, including your ideas, descriptions, and tasks, is encrypted client-side before 
                  being stored. Only you can decrypt and read your data.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>End-to-end encryption for all idea data</li>
                  <li>Secure authentication with Google OAuth or email/password</li>
                  <li>User-specific data isolation</li>
                  <li>Your encryption key never leaves your device</li>
                </ul>
              </div>
            </section>

            {/* Getting Started */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                Getting Started
              </h2>
              <ol className="list-decimal pl-6 space-y-3 text-gray-700 dark:text-gray-300">
                <li>Sign up for a free account using your email, Google, or Apple account</li>
                <li>Type your idea in the quick capture bar at the top of the home page</li>
                <li>Choose how to develop it:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Autopilot</strong> — Let AI fully develop your idea (requires entitlement)</li>
                    <li><strong>Guided Journey</strong> — Collaborate with AI step-by-step (requires entitlement)</li>
                    <li><strong>Raw Save</strong> — Save immediately and develop later (no AI credit)</li>
                  </ul>
                </li>
                <li>Add tasks, tags, and resources to organize your ideas</li>
                <li>Track your progress by updating status and ratings</li>
                <li>Filter and sort your ideas to find what you need quickly</li>
              </ol>
            </section>

            {/* Call to Action */}
            <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  Ready to Start?
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  Join Epiphoric today and start capturing your ideas securely.
                </p>
                <Link
                  to="/auth"
                  className="inline-block bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-md transition-colors"
                >
                  Get Started
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HowItWorks;

